import { useUserAgentDetection } from "@shared/hooks/useUserAgentDetection/useUserAgentDetection";
import useWindowSize from "@shared/hooks/useWindowSize";
import { useTranslation } from "@shared/utils/internationalization";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useCallback, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";
import {
  BASE_SELECTION_EVENT,
  DatePickerMode,
  DatePickerSelection,
} from "../DatePicker/DatePicker.props";
import DatePicker from "../DatePicker/DatePicker.web";
import DatePickerQuickOptions from "../DatePickerQuickOptions/DatePickerQuickOptions.web";
import DatePickerRangeContent from "../DatePickerRangeContent/DatePickerRangeContent.web";
import { DatePickerModalProps } from "./DatePickerModal.props";

export default function DatePickerModal({
  isOpen,
  onClose,
  showQuickMenu = true,
  onApply,
  customMenuOptions,
  numberOfMonths = 2,
  modalProps,
  ...datePickerProps
}: DatePickerModalProps) {
  const { mode, onChangeSelected } = datePickerProps;
  const isMobile = useUserAgentDetection();

  const { t } = useTranslation("common");
  const [selected, setSelected] = useState<DatePickerSelection | undefined>(
    datePickerProps.selected || undefined
  );
  const [month, setMonth] = useState<Date | undefined>(undefined);
  const { width: windowWidth } = useWindowSize();

  // 880 is the point where the date picker stacks 2 months instead of showing them side by side
  const isSmallerThan880 = useMemo(
    () => windowWidth && windowWidth < 880 - 1,
    [windowWidth]
  );

  const modalSize = useMemo(() => {
    if (isMobile || isSmallerThan880) {
      return "full";
    }

    if (showQuickMenu) {
      return "4xl";
    }

    return "3xl";
  }, [isMobile, isSmallerThan880, showQuickMenu]);

  // Convert internal state to Date | null for RangeContent
  const currentStartDate = useMemo(() => {
    if (mode === DatePickerMode.range && selected) {
      return (selected as DateRange)?.from ?? null;
    }
    return null;
  }, [mode, selected]);

  const currentEndDate = useMemo(() => {
    if (mode === DatePickerMode.range && selected) {
      return (selected as DateRange)?.to ?? null;
    }
    return null;
  }, [mode, selected]);

  // Handle dates change from RangeContent
  const handleDatesChange = useCallback(
    (dates: { startDate: Date | null; endDate: Date | null }) => {
      const newSelected: DateRange = {
        from: dates.startDate ?? undefined,
        to: dates.endDate ?? undefined,
      };
      setSelected(newSelected);
      onChangeSelected?.({
        selected: newSelected,
        triggerDate: dates.startDate ?? new Date(),
        modifiers: {},
        e: {} as React.MouseEvent,
      });
    },
    [onChangeSelected]
  );

  // Handle clear button
  const handleClear = useCallback(() => {
    setSelected(undefined);
    onChangeSelected?.(BASE_SELECTION_EVENT);
  }, [onChangeSelected]);

  // For range mode, use DatePickerRangeContent
  if (mode === DatePickerMode.range) {
    return (
      <Modal
        {...modalProps}
        isOpen={isOpen}
        onClose={onClose}
        data-testid="date-picker-modal"
        size={modalSize}
      >
        <Modal.Header>
          <Text>{t("Calendar")}</Text>
        </Modal.Header>

        <Modal.Body
          p="none"
          padding="none"
          paddingLeft="xl"
          paddingRight="xl"
          data-testid="date-picker-modal-body"
          display={{
            sm: "flex",
          }}
          flexDirection={{
            sm: "column",
          }}
          height={{
            sm: "100%",
          }}
        >
          <Flex flex="1" justifyContent="center">
            <DatePickerRangeContent
              currentStartDate={currentStartDate}
              currentEndDate={currentEndDate}
              onDatesChange={handleDatesChange}
              showQuickMenu={showQuickMenu}
              customQuickMenuOptions={customMenuOptions}
              numberOfMonths={isSmallerThan880 ? 1 : numberOfMonths}
              compact={isSmallerThan880 || isMobile}
              flexDirection={isSmallerThan880 || isMobile ? "column" : "row"}
            />
          </Flex>
        </Modal.Body>

        <Modal.Footer>
          <Button.Solid
            onClick={() =>
              onApply?.(
                currentStartDate ?? undefined,
                currentEndDate ?? undefined
              )
            }
            data-testid="date-picker-modal-apply-button"
            width="180px"
          >
            {t("Apply")}
          </Button.Solid>
          <Button.Ghost
            data-testid="date-picker-modal-cancel-button"
            onClick={handleClear}
          >
            {t("Clear")}
          </Button.Ghost>
        </Modal.Footer>
      </Modal>
    );
  }

  // For single/multiple mode, render original implementation
  // (RangeContent is specifically for range mode)
  return (
    <Modal
      {...modalProps}
      isOpen={isOpen}
      onClose={onClose}
      data-testid="date-picker-modal"
      size={modalSize}
    >
      <Modal.Header>
        <Text>{t("Calendar")}</Text>
      </Modal.Header>

      <Modal.Body
        p="none"
        padding="none"
        paddingLeft="xl"
        paddingRight="xl"
        data-testid="date-picker-modal-body"
        display={{
          sm: "flex",
        }}
        flexDirection={{
          sm: "column",
        }}
        height={{
          sm: "100%",
        }}
      >
        <Flex
          flex="1"
          flexDirection={{
            base: "column",
            sm: "row",
          }}
          justifyContent="center"
        >
          {showQuickMenu && (
            <DatePickerQuickOptions
              setMonth={setMonth}
              setSelection={setSelected}
              mode={mode}
              onChangeSelected={(event) => onChangeSelected?.(event)}
              customMenuOptions={customMenuOptions}
            />
          )}

          <Box
            padding={{
              base: "none",
              sm: "lg",
            }}
            width={{
              base: "fit-content",
              sm: "none",
            }}
            m={{
              base: "auto",
              sm: "none",
            }}
          >
            <DatePicker
              {...datePickerProps}
              onChangeSelected={(update) => {
                setSelected(update.selected);
                onChangeSelected?.(update);
              }}
              selected={selected}
              month={month || (selected as DateRange)?.from || new Date()}
              onMonthChange={setMonth}
              data-testid="date-picker"
              numberOfMonths={isSmallerThan880 ? 1 : numberOfMonths}
            />
          </Box>
        </Flex>
      </Modal.Body>
    </Modal>
  );
}
